import type {Barcode} from './barcode';
import type {StreamBarcodeScanner} from './stream-barcode-scanner';

/**
 * @name BarcodeFormat
 * @module portaro.barcode-scanner
 * @kind enum
 *
 * @description
 * Supported barcode formats
 */
export enum BarcodeFormat {
    ANY = '',
    AZTEC = 'AZTEC',
    CODABAR = 'CODABAR',
    CODE_39 = 'CODE_39',
    CODE_93 = 'CODE_93',
    CODE_128 = 'CODE_128',
    DATA_MATRIX = 'DATA_MATRIX',
    EAN_8 = 'EAN_8',
    EAN_13 = 'EAN_13',
    ITF = 'ITF',
    MAXICODE = 'MAXICODE',
    PDF_417 = 'PDF_417',
    QR_CODE = 'QR_CODE',
    RSS_14 = 'RSS_14',
    RSS_EXPANDED = 'RSS_EXPANDED',
    UPC_A = 'UPC_A',
    UPC_E = 'UPC_E',
    UPC_EAN_EXTENSION = 'UPC_EAN_EXTENSION'
}

/**
 * @name BarcodeDecoder
 * @module portaro.barcode-scanner
 * @kind interface
 *
 * @description
 * Barcode decoder decodes barcodes from images
 * It only decodes one image at the time
 */
export interface BarcodeDecoder {
    decodeImage(image: ImageData, barcodeFormats: BarcodeFormat[]): Promise<Barcode>;
}

/**
 * @name BarcodeScanner
 * @module portaro.barcode-scanner
 * @kind interface
 *
 * @description
 * Barcode scanner manages barcode scanning (image sampling, decoding)
 * It outputs barcodes via callback function.
 */
export interface BarcodeScanner {
    startScanner(onResult: (result: Barcode) => void): Promise<void>

    stopScanner(): void

    isStarted(): boolean
}

/**
 * @name ScannerSettings
 * @module portaro.barcode-scanner
 * @kind interface
 *
 * @description
 * Scanner settings
 */
export interface ScannerSettings {
    readonly timeBetweenScansInMillis: number;
    readonly timeBetweenDecodeInMillis: number;
    readonly scanWidth: number;
    readonly scanHeight: number;
    readonly codeFormats: BarcodeFormat[];
    readonly tryHarder: boolean;
    readonly allowEmptyResult: boolean;
}

export interface ScannerFactory {
    createScanner(videoSourceElement: HTMLVideoElement, settings?: ScannerSettings): StreamBarcodeScanner
}

export enum ScannerState {
    READY_TO_OPERATE,
    STARTING,
    OPERATING,
    WAITING,
    ERRORED
}

export enum BarcodeScannerViewerState {
    OPENED = 'open',
    CLOSED = 'closed'
}